import { useContext } from 'react'
import { useStore } from 'zustand'

import { PageStoreContext } from './PageStoreContext'

import type { BaseUIState, PageStoreState } from './types'

/**
 * 消费页面 Store 的 hook
 *
 * 使用 selector 精确订阅，只有用到的字段变化时才触发 rerender
 *
 * @example
 * ```ts
 * // 订阅单个字段
 * const mode = usePageStore((s) => s.form.mode)
 *
 * // 订阅 action
 * const setUI = usePageStore((s) => s.setUI)
 *
 * // 订阅多个字段（自动 shallow compare）
 * const { isUploading, isDetecting } = usePageStore((s) => ({
 *   isUploading: s.ui.isUploading,
 *   isDetecting: s.ui.isDetecting,
 * }))
 * ```
 */
export function usePageStore<
  TForm extends Record<string, any>,
  TPrefs extends Record<string, any>,
  TUI extends BaseUIState,
  U,
>(selector: (state: PageStoreState<TForm, TPrefs, TUI>) => U): U {
  const store = useContext(PageStoreContext)
  if (!store) {
    throw new Error(
      'usePageStore must be used within a <PageStoreProvider>. ' +
        'Wrap your page component with PageStoreProvider.',
    )
  }
  return useStore(store, selector)
}
